import React, { useEffect, useState, useMemo } from 'react';
import { Box,
    Typography,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TableSortLabel } from '@mui/material';
import { CSVLink } from 'react-csv';
import { useDispatch, useSelector } from 'react-redux';
import { CustomButton } from '../../../shared';
import { ArrowLeftIcon, ArrowRightIcon, DownloadIcon } from '../../../../assets';
import TableRowsSkeleton from '../../../shared/skelton/tableSkelton';
import { actions as webMatrixActions } from '../../../../redux/reducers/webMatrixReducer';
import { calculateMaxWidth,
    getSortedData,
    handleRowClassForEngagementsTable } from '../../../../utility';
import { getWebMatrixPatientAnalysisSummary } from '../../../../redux/thunks/webMatrixThunks';

const NPITable = () => {
    const dispatch = useDispatch();
    const { dtcData } = useSelector((state) => state.webMatrix);
    const { company } = useSelector((state) => state.company);
    const { startDateRange, endDateRange } = useSelector((state) => state.filter);

    const {
        dtcTableData = [],
        filteredDtcTableData = [],
        isWebMatrixPatientSummaryLoading,
        currentPage = 1,
        pageSize = 10,
        totalPages = 0,
        totalRecordsFetch = 0
    } = dtcData;

    const { setCurrentPage,resetTableStates } = webMatrixActions;

    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState(null);
    const [maxWidth, setMaxWidth] = useState(calculateMaxWidth());
    const headers = useMemo(
        () => [
            { label: 'Date', key: 'date' },
            { label: 'Audience Type', key: 'audience_type' },
            // { label: 'Site Name', key: 'site_name' },
            { label: 'Creative Asset', key: 'creative_asset' },
            { label: 'Channel', key: 'channel' },
            { label: 'Vendor', key: 'vendor' },
            { label: 'Impressions', key: 'impressions' },
            { label: 'Clicks', key: 'clicks' }
        ],
        []
    );

    const csvData = useMemo(() => dtcTableData.map((provider) => ({
        date: provider?.date || '',
        audience_type: provider?.audience_type || '',
        creative_asset: provider?.creative_asset || '',
        site_name: provider?.site_name || '',
        channel: provider?.channel || '',
        vendor: provider?.vendor || '',
        impressions: provider?.impressions || 0,
        clicks: provider?.clicks || 0
    })), [dtcTableData]);

    const sortedData = useMemo(
        () => getSortedData(orderBy, order, filteredDtcTableData),
        [order, orderBy, filteredDtcTableData]
    );

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handlePageChange = (event, page) => {
        dispatch(setCurrentPage(page));
    };

    const handleResize = () => setMaxWidth(calculateMaxWidth());
    useEffect(() => {
        dispatch(resetTableStates());
        if (company.id) {
            dispatch(
                getWebMatrixPatientAnalysisSummary({
                    companyId: company.id,
                    startRange: startDateRange,
                    endRange: endDateRange,
                    page_no: currentPage,
                    page_size: 20
                })
            );
        }
    }, [company, startDateRange, endDateRange,currentPage]);
    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const renderTableData = () => {
        if (totalRecordsFetch === 0) {
            return (
                <TableRow>
                    <TableCell colSpan={8}>
                        <Box p={3} textAlign="center">
                            <Typography className="text-sm-regular gray-900">
                                No Providers data to display.
                            </Typography>
                        </Box>
                    </TableCell>
                </TableRow>
            );
        }

        return sortedData.map((row, index) => (
            <TableRow
                className={handleRowClassForEngagementsTable(index, company?.id)}
                key={index}
            >
                <TableCell className="table-row-data no-border wh_space_overflow_h_text_of">{row?.date}</TableCell>
                <TableCell className="table-row-data no-border">{row?.audience_type}</TableCell>
                {/* <TableCell className="table-row-data no-border">{row?.site_name}</TableCell> */} 
                <TableCell className="table-row-data no-border">{row?.creative_asset}</TableCell>
                <TableCell className="table-row-data no-border">{row?.channel}</TableCell>
                <TableCell className="table-row-data no-border">{row?.vendor}</TableCell>
                <TableCell className="table-row-data no-border">{row?.impressions}</TableCell>
                <TableCell className="table-row-data no-border">{row?.clicks}</TableCell>
            </TableRow>
        ));
    };

    return (
        <Box className="b1px-border-box-br8px" sx={{ maxWidth: maxWidth, width: '100%', minWidth: 0 }}>
            <Box className="padding-tb-20-rl-24-bb-1px">
                <Box className="flex-row-space-between-center">
                    <Typography className="text-md-medium gray-900">See Full List Below</Typography>
                    {/* this need to be fixed by api then we will add this functionality */}
                    {/* <CSVLink data={csvData} filename="Paid_Media_Engagement.csv" headers={headers}>
                        <CustomButton
                            buttonColor="#AD509E"
                            icon={<DownloadIcon />}
                            title="Export All Data"
                            titleColor="#FFFFFF"
                        />
                    </CSVLink> */}
                </Box>
            </Box>
            <TableContainer className="no-box-shadow no-border" sx={{ overflowX: 'auto' }} component={Paper}>
                <Table className="table-border-collapse no-border">
                    <TableHead>
                        <TableRow>
                            {headers.map(({ label, key }) => (
                                <TableCell className="table-headers-title" key={key}>
                                    <TableSortLabel
                                        active={orderBy === key}
                                        aria-label={`Sort by ${label}`}
                                        direction={orderBy === key ? order : 'asc'}
                                        onClick={() => handleRequestSort(key)}
                                    >
                                        {label}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isWebMatrixPatientSummaryLoading ? (
                            <TableRowsSkeleton columns={7} />
                        ) : (
                            renderTableData()
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {totalPages > 0 && (
                <Box className="padding-tb-16-rl-24 border-top-gray">
                    <Box className="flex-row-space-between-center">
                        <CustomButton
                            buttonColor="#FFFFFF"
                            buttonDisabled={currentPage === 1}
                            handleClick={() => handlePageChange(null, currentPage - 1)}
                            icon={<ArrowLeftIcon />}
                            title="Previous"
                            titleColor="#344054"
                        />
                        <Pagination
                            hideNextButton
                            hidePrevButton
                            count={totalPages}
                            page={currentPage}
                            shape="rounded"
                            onChange={handlePageChange}
                        />
                        <CustomButton
                            buttonColor="#FFFFFF"
                            buttonDisabled={currentPage === totalPages}
                            handleClick={() => handlePageChange(null, currentPage + 1)}
                            tailIcon={<ArrowRightIcon />}
                            title="Next"
                            titleColor="#344054"
                        />
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default NPITable;
